/**
 * Service: PortfolioAllocationsService
 *
 * Centralizes reads/writes and reporting helpers for `portfolio_allocations`.
 * Used by portfolio UI/hooks and manual asset flows. Includes per-portfolio
 * queries, per-asset lookups, user-scoped reports, and bulk operations.
 */
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';
import type { TablesInsert } from '@/integrations/supabase/types';

export async function findAllocationId(portfolioId: string, assetId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('portfolio_allocations')
    .select('id')
    .eq('portfolio_id', portfolioId)
    .eq('asset_id', assetId)
    .limit(1);

  if (error) {
    logger.error('PortfolioAllocationsService', 'findAllocationId failed', { error, portfolioId, assetId });
    throw error;
  }

  return (data && data.length > 0) ? (data[0] as any).id : null;
}

export async function findExistingAllocationAssetIds(
  portfolioId: string,
  candidateAssetIds: string[]
): Promise<string[]> {
  if (!candidateAssetIds || candidateAssetIds.length === 0) return [];
  const { data, error } = await supabase
    .from('portfolio_allocations')
    .select('asset_id')
    .eq('portfolio_id', portfolioId)
    .in('asset_id', candidateAssetIds);
  if (error) {
    logger.error('PortfolioAllocationsService', 'findExistingAllocationAssetIds failed', { error, portfolioId, count: candidateAssetIds.length });
    throw error;
  }
  return ((data as any[]) || []).map((r: any) => String(r.asset_id));
}

export async function fetchFundAllocationsForPortfolio(
  portfolioId: string
): Promise<Array<{
  id: string;
  asset_id: string;
  asset_type: string;
  allocation_percentage: number;
  funds: { id: string; fund_name: string; fund_strategy: string | null; fund_type: string | null };
}>> {
  const { data, error } = await supabase
    .from('portfolio_allocations' as any)
    .select(`
      id,
      asset_id,
      asset_type,
      allocation_percentage,
      funds!inner(
        id,
        fund_name,
        fund_strategy,
        fund_type
      )
    `)
    .eq('portfolio_id', portfolioId)
    .eq('asset_type', 'fund_raw');

  if (error) {
    logger.error('PortfolioAllocationsService', 'fetchFundAllocationsForPortfolio failed', { error, portfolioId });
    throw error;
  }
  return (data as any[]) || [];
}

export async function fetchPortfolioAllocationsForAssetForUser(
  assetId: string,
  userId: string,
  assetType?: string
): Promise<Array<{ portfolio_id: string; portfolio_name: string; allocation_percentage: number }>> {
  // Build base query with join to portfolios to filter by user_id
  const base = supabase
    .from('portfolio_allocations' as any)
    .select(
      `allocation_percentage, portfolio_id, portfolios:portfolio_id (id, name)`
    )
    .eq('asset_id', assetId)
    .eq('portfolios.user_id', userId) as any;

  let query = base;
  if (assetType) {
    if (assetType === 'fund_raw') {
      // Support legacy 'fund' and null types for historical allocations
      query = query.or('asset_type.eq.fund_raw,asset_type.eq.fund,asset_type.is.null');
    } else {
      query = query.eq('asset_type', assetType);
    }
  }

  const { data, error } = await query;
  if (error) {
    logger.error('PortfolioAllocationsService', 'fetchPortfolioAllocationsForAssetForUser failed', { error, assetId, userId, assetType });
    throw error;
  }
  const rows = (data as any[]) || [];
  return rows.map((r: any) => ({
    portfolio_id: r?.portfolios?.id || r?.portfolio_id,
    portfolio_name: r?.portfolios?.name || '',
    allocation_percentage: r?.allocation_percentage ?? 0,
  }));
}

export async function deleteAllocationsByAssetId(assetId: string): Promise<void> {
  const { error } = await supabase
    .from('portfolio_allocations')
    .delete()
    .eq('asset_id', assetId);
  if (error) {
    logger.error('PortfolioAllocationsService', 'deleteAllocationsByAssetId failed', { error, assetId });
    throw error;
  }
}

export async function findUserPortfolioNamesForAsset(assetId: string, userId: string): Promise<{ count: number; names: string[] }> {
  const { data, error } = await supabase
    .from('portfolio_allocations')
    .select(`
      id,
      portfolios!inner(name, user_id)
    `)
    .eq('asset_id', assetId);
  if (error) {
    logger.error('PortfolioAllocationsService', 'findUserPortfolioNamesForAsset failed', { error, assetId, userId });
    throw error;
  }
  const names = ((data as any[]) || [])
    .filter((row: any) => row?.portfolios?.user_id === userId)
    .map((row: any) => row?.portfolios?.name)
    .filter(Boolean);
  const unique = [...new Set(names)];
  return { count: unique.length, names: unique };
}

export async function findUserPortfolioNamesForAssets(assetIds: string[], userId: string): Promise<Record<string, { count: number; names: string[] }>> {
  if (!assetIds || assetIds.length === 0) return {};
  const { data, error } = await supabase
    .from('portfolio_allocations')
    .select(`
      id,
      asset_id,
      portfolios!inner(name, user_id)
    `)
    .in('asset_id', assetIds);
  if (error) {
    logger.error('PortfolioAllocationsService', 'findUserPortfolioNamesForAssets failed', { error, count: assetIds.length, userId });
    throw error;
  }
  const grouped: Record<string, string[]> = {};
  ((data as any[]) || []).forEach((row: any) => {
    const aid = String(row?.asset_id || '');
    const isUser = row?.portfolios?.user_id === userId;
    if (!aid || !isUser) return;
    const name = row?.portfolios?.name || '';
    if (!grouped[aid]) grouped[aid] = [];
    if (name) grouped[aid].push(name);
  });
  const result: Record<string, { count: number; names: string[] }> = {};
  Object.keys(grouped).forEach((aid) => {
    const unique = [...new Set(grouped[aid])];
    result[aid] = { count: unique.length, names: unique };
  });
  return result;
}

export async function fetchAllocationAssetIds(portfolioId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('portfolio_allocations')
    .select('asset_id')
    .eq('portfolio_id', portfolioId);
  if (error) {
    logger.error('PortfolioAllocationsService', 'fetchAllocationAssetIds failed', { error, portfolioId });
    throw error;
  }
  return ((data as any[]) || []).map((a: any) => String(a.asset_id));
}

export async function deleteAllocationById(id: string): Promise<void> {
  const { error } = await supabase
    .from('portfolio_allocations')
    .delete()
    .eq('id', id);
  if (error) {
    logger.error('PortfolioAllocationsService', 'deleteAllocationById failed', { error, id });
    throw error;
  }
}

export async function deleteAllocationsBulk(ids: string[]): Promise<void> {
  if (!ids || ids.length === 0) return;
  const { error } = await supabase
    .from('portfolio_allocations')
    .delete()
    .in('id', ids);
  if (error) {
    logger.error('PortfolioAllocationsService', 'deleteAllocationsBulk failed', { error, ids });
    throw error;
  }
}

export async function insertAllocationsBulk(payloads: TablesInsert<'portfolio_allocations'>[]): Promise<void> {
  if (!payloads || payloads.length === 0) return;
  const { error } = await supabase
    .from('portfolio_allocations')
    .insert(payloads);
  if (error) {
    logger.error('PortfolioAllocationsService', 'insertAllocationsBulk failed', { error, count: payloads.length });
    throw error;
  }
}

export async function fetchAllAllocations(portfolioId: string): Promise<any[]> {
  const { data, error } = await supabase
    .from('portfolio_allocations')
    .select('*')
    .eq('portfolio_id', portfolioId);
  if (error) {
    logger.error('PortfolioAllocationsService', 'fetchAllAllocations failed', { error, portfolioId });
    throw error;
  }
  return (data as any[]) || [];
}

export async function updateAllocationFields(id: string, fields: Partial<{ allocation_value: number | null; allocation_percentage: number | null; updated_at: string }>): Promise<void> {
  const { error } = await supabase
    .from('portfolio_allocations')
    .update(fields as any)
    .eq('id', id);
  if (error) {
    logger.error('PortfolioAllocationsService', 'updateAllocationFields failed', { error, id, fields });
    throw error;
  }
}

export async function fetchAllocationsForCompletion(portfolioId: string): Promise<Array<{ allocation_percentage: number | null; allocation_value: number | null }>> {
  const { data, error } = await supabase
    .from('portfolio_allocations')
    .select('allocation_percentage, allocation_value')
    .eq('portfolio_id', portfolioId);

  if (error) {
    logger.error('PortfolioAllocationsService', 'fetchAllocationsForCompletion failed', { error, portfolioId });
    throw error;
  }
  return (data as any[]) || [];
}

export async function insertAllocation(payload: TablesInsert<'portfolio_allocations'>): Promise<void> {
  const { error } = await supabase
    .from('portfolio_allocations')
    .insert(payload);

  if (error) {
    logger.error('PortfolioAllocationsService', 'insertAllocation failed', { error, payload });
    throw error;
  }
}

export async function findAllocationIdWithType(portfolioId: string, assetId: string, assetType: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('portfolio_allocations')
    .select('id')
    .eq('portfolio_id', portfolioId)
    .eq('asset_id', assetId)
    .eq('asset_type', assetType)
    .limit(1);

  if (error) {
    logger.error('PortfolioAllocationsService', 'findAllocationIdWithType failed', { error, portfolioId, assetId, assetType });
    throw error;
  }

  return (data && data.length > 0) ? (data[0] as any).id : null;
}
